import { skipToken, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query"
import axios from "axios"
import config from "config"
import type { Customer, CustomerId } from "types/customer"
import type { Profile } from "./useAuth0Metadata"
import { myCustomersQueryKey, type MyCustomersResponse } from "./useMyCustomers"
import { sherlockQueryKey } from "./useSherlock"
import useAccessToken from "./useAccessToken"

// GET /me/bootstrap: everything the app waits for before its first screen —
// the profile, the /me/customers payload and the active customer's config —
// in one round trip instead of three in a row. Each part lands in the cache
// entry its own hook reads (useAuth0Metadata, useMyCustomers, useSherlock),
// so every consumer and every later refetch works exactly as before. Those
// hooks hold their own requests until this one settles; if it fails (an API
// without the route, an outage) they fetch their parts themselves.

export type BootstrapResponse = {
  // null when rr-api could not reach Auth0 — useAuth0Metadata then asks itself
  // (a snapshot's copy is invalidated so it does)
  profile: Profile | null
  customers: MyCustomersResponse
  // the customer the config belongs to (null: none usable / not loaded)
  customerId: CustomerId | null
  customer: Customer | null
}

export const bootstrapQueryKey = ["me-bootstrap"]
export const auth0UserQueryKey = ["auth0-user"]

const meUrl = `${config.railApiBasePath}/me`

/** Put each part of a bootstrap answer where its own hook reads it. */
export const seedBootCaches = (queryClient: QueryClient, boot: BootstrapResponse, updatedAt?: number) => {
  if (boot.profile) queryClient.setQueryData(auth0UserQueryKey, boot.profile, { updatedAt })
  queryClient.setQueryData(myCustomersQueryKey, boot.customers, { updatedAt })
  if (boot.customerId && boot.customer) queryClient.setQueryData(sherlockQueryKey(boot.customerId), boot.customer, { updatedAt })
}

type Props = {
  enabled: boolean
  // ?customer= (an emailed link) or the dev customerId pin: the server
  // returns that customer's config when the account may use it
  preferredCustomer?: CustomerId | null
}

export const useBootstrap = ({ enabled, preferredCustomer }: Props) => {
  const token = useAccessToken()
  const queryClient = useQueryClient()

  return useQuery({
    queryKey: bootstrapQueryKey,
    queryFn: async () => {
      const startedAt = Date.now()
      try {
        const { data } = await axios.get<BootstrapResponse>(`${meUrl}/bootstrap`, {
          params: preferredCustomer ? { customer: preferredCustomer } : undefined,
          headers: { Authorization: `Bearer ${token}` },
        })
        // A preference written while this was in flight (App's lastOpenedAt,
        // rendered from a boot snapshot) is newer than the profile in here.
        const profileWrittenSince = (queryClient.getQueryState(auth0UserQueryKey)?.dataUpdatedAt ?? 0) > startedAt
        // before this query settles: the waiting hooks find their data in place
        seedBootCaches(queryClient, profileWrittenSince ? { ...data, profile: null } : data)
        // Parts the server could not answer: whatever the cache holds for
        // them (a boot snapshot, up to 14 days old, never stale by time) must
        // be re-read by its own hook once released.
        if (!data.profile && !profileWrittenSince) void queryClient.invalidateQueries({ queryKey: auth0UserQueryKey, refetchType: "none" })
        if (!data.customer) void queryClient.invalidateQueries({ queryKey: ["sherlock"], refetchType: "none" })
        return data
      } catch (error) {
        // Anything already cached for these (a boot snapshot) is now of
        // unknown age: let each hook re-read its part once it is released.
        for (const queryKey of [auth0UserQueryKey, myCustomersQueryKey, ["sherlock"]]) {
          void queryClient.invalidateQueries({ queryKey, refetchType: "none" })
        }
        throw error
      }
    },
    enabled: enabled && !!token,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    // one attempt: on failure the separate calls take over straight away
    retry: false,
  })
}

// Entries about the signed-in account rather than a customer: they survive a
// customer switch. /me/bootstrap is once per page load (dropping it would make
// the mounted useBootstrap fetch it again, with the mount-time ?customer= link,
// and hold the other boot hooks behind it); the profile gates the app
// (AppWrapper) and doesn't depend on the customer; the token and the account's
// customer list (the picker keeps its current_customer_id in step) neither.
const ACCOUNT_LEVEL_QUERY_ROOTS = new Set<unknown>([bootstrapQueryKey[0], auth0UserQueryKey[0], "auth0-access-token", myCustomersQueryKey[0]])

/**
 * Empty the cache for a customer switch — page queries are not keyed by
 * customer — keeping the account-level entries above.
 */
export const clearCustomerScopedCaches = (queryClient: QueryClient) => {
  queryClient.removeQueries({ predicate: (query) => !ACCOUNT_LEVEL_QUERY_ROOTS.has(query.queryKey[0]) })
  queryClient.getMutationCache().clear()
}

/**
 * Whether GET /me/bootstrap has answered (success or error). Observes the
 * CustomerProvider's request without ever starting one.
 */
export const useBootstrapSettled = () => {
  const { isSuccess, isError } = useQuery({ queryKey: bootstrapQueryKey, queryFn: skipToken })
  return isSuccess || isError
}
